<script setup lang="ts">
/**
 * SfgSurfaceTexture
 *
 * Drops the T09 treatment onto a surface:
 *   chalk -> a single soft grain overlay
 *   ink   -> a soft grain + a sharp speckle overlay stacked
 *
 * Chalk surfaces keep deliberately lighter than ink. Layering two
 * noise filters on cream warmed the page toward tan; the single
 * light grain lets chalk pages stay bright while still feeling like
 * paper up close.
 *
 * Parent must be `position: relative` and `overflow: hidden`. Parent
 * content should sit in its own `relative z-10` wrapper so it paints
 * above the texture layer.
 *
 * Tune alpha in SfgTextureDefs' feColorMatrix:
 *   chalk grain:   0.06 (calm) - 0.12 (loud). Currently 0.08.
 *   ink grain:     0.08 (calm) - 0.14 (loud). Currently 0.11.
 *   ink speckle:   0.10 (calm) - 0.18 (loud). Currently 0.14.
 *
 * Usage:
 *   <section class="relative overflow-hidden bg-brand-chalk">
 *     <SfgSurfaceTexture variant="chalk"/>
 *     <div class="relative z-10">...content...</div>
 *   </section>
 */

withDefaults(defineProps<{
  variant?: 'chalk' | 'ink'
}>(), {
  variant: 'chalk',
})
</script>

<template>
  <svg
    aria-hidden="true"
    class="pointer-events-none absolute inset-0 h-full w-full"
    preserveAspectRatio="none"
  >
    <!-- Chalk: single soft grain -->
    <rect
      v-if="variant === 'chalk'"
      width="100%"
      height="100%"
      filter="url(#sfg-grain-chalk)"
    />

    <!-- Ink: full two-layer T09 (grain + speckle) -->
    <template v-if="variant === 'ink'">
      <rect
        width="100%"
        height="100%"
        filter="url(#sfg-grain-ink)"
      />
      <rect
        width="100%"
        height="100%"
        filter="url(#sfg-speckle-ink)"
      />
    </template>
  </svg>
</template>
